import React, { useId, useMemo } from 'react'
import styled from 'styled-components'

import { NotificationDotIcon } from '@blade/ui-kit'
import { ImagePopover } from '@root/shared/ui'
import { Avatar } from '@root/shared/ui-kit'

import { UserShort } from '../model'

import type { UserMinimal } from '../model'
import type { ClassNameProps } from '@root/shared/react'
import type { AvatarProps } from '@root/shared/ui-kit'

const AVATAR_SIZE = 40

type UserAvatarHypedProps = {
  withHypedLetter?: boolean
  outlineWidth?: number
  outlineSpacingWidth?: number
  withPreview?: boolean
}

type UserAvatarProps = {
  userShort?: UserShort.UserShort | UserMinimal.UserMinimal
} & Pick<AvatarProps, 'size'> & {
    isOnline?: boolean
    onClick?: () => void
  } & ClassNameProps &
  UserAvatarHypedProps

function stringToHue(str: string) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = Math.trunc(hash)
  }

  // Приводим хеш к диапазону [0, 360)
  const hue = (Math.abs(hash) % 360) + Math.abs(hash % 1000) / 1000 // добавим 3 знака точности
  return parseFloat(hue.toFixed(3))
}

const UserShortAvatarMemo = React.memo<UserAvatarProps>(
  ({
    userShort,
    className,
    isOnline = false,
    withHypedLetter = false,
    outlineWidth = 0,
    outlineSpacingWidth = 0,
    withPreview = false,
    ...props
  }) => {
    const size = props.size
    const avatarSize = size ?? AVATAR_SIZE
    const initials = useMemo(
      () =>
        userShort && 'firstName' in userShort
          ? UserShort.initials(userShort) || '??'
          : '??',
      [userShort],
    )
    const colorBase = userShort?.delugeId
    const color = useMemo(() => {
      if (!colorBase) {
        return 'var(--background-7)'
      }
      const from = stringToHue(colorBase)
      return `linear-gradient(180deg, hsl(${from}deg 80% 60%) 0%, hsl(${from}deg 65% 45%) 100%)`
    }, [colorBase])

    const avatar = (
      <Root data-hyped={withHypedLetter}>
        {userShort &&
        'referralCodeStatus' in userShort &&
        userShort.referralCodeStatus === 'Hyped' ? (
          <HypedOutline
            size={size}
            withHypedLetter={withHypedLetter}
            outlineWidth={outlineWidth}
            outlineSpacingWidth={outlineSpacingWidth}
          />
        ) : undefined}
        <Avatar
          className={className}
          fallback={initials}
          color={color}
          src={userShort?.avatarSrc}
          retryDelay={30}
          {...props}
          style={{
            zIndex: -1,
          }}
        />
        {isOnline ? (
          <PresenceDot $avatarSize={avatarSize} aria-hidden>
            <NotificationDotIcon variant="success" glowing />
          </PresenceDot>
        ) : undefined}
      </Root>
    )

    return withPreview && userShort?.avatarSrc ? (
      <ImagePopover src={userShort.avatarSrc}>{avatar}</ImagePopover>
    ) : (
      avatar
    )
  },
)

export const UserShortAvatar = Object.assign(UserShortAvatarMemo, {
  HypedOutline,
  HypedLetter,
})

UserShortAvatar.displayName = 'UserShortAvatar'

const Root = styled.div`
  display: inline-flex;
  position: relative;
  isolation: isolate;
  overflow: visible;
`

const PRESENCE_LAYOUT_HALF_PX = 3
const PRESENCE_CENTER_INSET_FRAC = (1 - Math.SQRT1_2) / 2
const PRESENCE_CENTER_INSET_PCT = PRESENCE_CENTER_INSET_FRAC * 100

const PresenceDot = styled.div<{ $avatarSize: number }>`
  right: calc(${PRESENCE_CENTER_INSET_PCT}% - ${PRESENCE_LAYOUT_HALF_PX}px);
  bottom: calc(${PRESENCE_CENTER_INSET_PCT}% - ${PRESENCE_LAYOUT_HALF_PX}px);
  transform: scale(${({ $avatarSize: S }) => S / AVATAR_SIZE});
  transform-origin: ${PRESENCE_LAYOUT_HALF_PX}px ${PRESENCE_LAYOUT_HALF_PX}px;
  position: absolute;
  line-height: 0;
`

function HypedOutline({
  size = AVATAR_SIZE,
  withHypedLetter = false,
  outlineWidth,
  outlineSpacingWidth,
}: UserAvatarHypedProps & Pick<AvatarProps, 'size'>) {
  const outlineWidthRatio = 0.8 / 54
  const badgeSizeRatio = 16 / 54

  const strokePx = outlineWidth ?? outlineWidthRatio * size
  const innerOffsetPx = outlineSpacingWidth ?? -strokePx
  const innerRadiusPx = size / 2 + innerOffsetPx
  const outerDiameterPx = 2 * (innerRadiusPx + strokePx)
  const ringOffsetPx = (size - outerDiameterPx) / 2
  // HypedRing is a circle centered in the shell; stroke midline radius from that center.
  const ringMidRadiusPx = innerRadiusPx + strokePx / 2

  // Integer box so the SVG maps to whole device pixels (avoids lopsided raster at size 20, etc.).
  const badgeSizePx = Math.max(1, Math.round(badgeSizeRatio * size))

  return (
    <HypedOutlineShell
      style={{
        top: `${ringOffsetPx}px`,
        left: `${ringOffsetPx}px`,
        width: `${outerDiameterPx}px`,
        height: `${outerDiameterPx}px`,
      }}
    >
      <HypedRing
        style={{
          ['--hyped-ring-inner' as string]: `${innerRadiusPx}px`,
        }}
      />
      {withHypedLetter ? (
        <HypedBadge
          style={{
            left: '50%',
            top: '50%',
            transform: `translate(-50%, -55%) translateY(${ringMidRadiusPx}px)`,
            width: `${badgeSizePx}px`,
            height: `${badgeSizePx}px`,
          }}
        >
          <HypedLetter />
        </HypedBadge>
      ) : undefined}
    </HypedOutlineShell>
  )
}

const HypedOutlineShell = styled.div`
  position: absolute;
  z-index: 0;
  isolation: isolate;
  pointer-events: none;
`

const HypedRing = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  border-radius: 50%;
  background: linear-gradient(
    180deg,
    #24efca 4.88%,
    #01c4de 52.88%,
    #00a5f1 100%
  );
  -webkit-mask: radial-gradient(
    circle at center,
    transparent calc(var(--hyped-ring-inner) - 0.5px),
    #000 calc(var(--hyped-ring-inner) + 0.5px)
  );
  mask: radial-gradient(
    circle at center,
    transparent calc(var(--hyped-ring-inner) - 0.5px),
    #000 calc(var(--hyped-ring-inner) + 0.5px)
  );

  opacity: 0.8;

  transition: opacity 0.2s ease-in-out;

  ${Root}[data-hyped='true']:hover & {
    opacity: 1;
  }
`

const HypedBadge = styled.div`
  position: absolute;
  pointer-events: none;
  line-height: 0;
  opacity: 0.95;

  transition: opacity 0.2s ease-in-out;

  ${Root}[data-hyped='true']:hover & {
    opacity: 1;
  }
`

function HypedLetter() {
  const uid = useId().replaceAll(':', '')
  const filterId = `${uid}-filter0_d`
  const paint0 = `${uid}-paint0_linear`
  const paint1 = `${uid}-paint1_radial`
  const paint2 = `${uid}-paint2_radial`

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      shapeRendering="geometricPrecision"
      style={{ display: 'block' }}
    >
      <path
        d="M11.8271 14.6279L4.17383 14.6279L0.346679 8L4.17383 1.37207L11.8271 1.37207L15.6543 8L11.8271 14.6279Z"
        fill={`url(#${paint0})`}
        stroke={`url(#${paint1})`}
        strokeWidth="0.6"
      />
      <path
        d="M15.332 8.00033L11.6654 14.3512L4.33203 14.3512L0.665365 8.00033L4.33203 1.64947L11.6654 1.64947L15.332 8.00033Z"
        fill={`url(#${paint2})`}
      />
      <g filter={`url(#${filterId})`}>
        <path
          d="M4.80078 11.1998V4.7998H6.5647V7.36855H9.44042V4.7998H11.2008V11.1998H9.44042V8.62793H6.5647V11.1998H4.80078Z"
          fill="#002336"
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="4.80078"
          y="4.7998"
          width="6.4"
          height="6.9"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.67 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result={`${uid}-effect1_dropShadow`}
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2={`${uid}-effect1_dropShadow`}
            result="shape"
          />
        </filter>
        <linearGradient
          id={paint0}
          x1="8"
          y1="-1.6"
          x2="8"
          y2="16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#00FFB7" />
          <stop offset="1" stopColor="#0091FF" />
        </linearGradient>
        <radialGradient
          id={paint1}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(8 20.8) rotate(-90) scale(22.8 22.8)"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id={paint2}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(7.9987 0.666992) rotate(90) scale(7.33334 18.7291)"
        >
          <stop stopColor="white" stopOpacity="0.75" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  )
}
